import {
  ActivityStatus,
  QActivitySequence,
  QButton,
  QStack,
  QText,
} from '@qualio/ui-components';
import { Parser } from 'html-to-react';
import { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '../../../../hooks/useFormatDate';

interface CommentThreadProps {
  thread: CommentsRepository.CommentThreadFetched;
  isMultiroot?: boolean;
}

interface ThreadCommentsProps {
  contentCommentedOnTargets: CommentsRepository.CommentThreadFetched['content_commented_on'];
  commentContent: ReactElement;
  commentIndex: number;
}

interface ContentCommentedOnProps {
  contentCommentedOnTargets: CommentsRepository.CommentThreadFetched['content_commented_on'];
}

const LETTER_LIMIT_CONTENT_COMMENTED_ON = 120;

export const parseCommentInThread = (
  thread: CommentsRepository.CommentThreadFetched,
  formatDate: (utcDate: Date | string, includeTime?: boolean) => string,
) => {
  const htmlToReactParser = new Parser();
  return thread.comments.map((comment, index: number) => ({
    status: 'commented' as ActivityStatus,
    fullName: comment.attributes.authorFullName ?? comment.user.id,
    actionTimestamp: formatDate(comment.created_at, true),
    children: (
      <ThreadComments
        contentCommentedOnTargets={thread.content_commented_on}
        commentContent={htmlToReactParser.parse(comment.content)}
        commentIndex={index}
      ></ThreadComments>
    ),
  }));
};

export const ContentCommentedOn = ({
  contentCommentedOnTargets,
}: ContentCommentedOnProps) => {
  const { t } = useTranslation();
  const [showAllContentCommentOn, setShowAllContentCommentOn] = useState(false);
  const toggleShowAllContentCommentedOn = () => {
    setShowAllContentCommentOn(!showAllContentCommentOn);
  };

  const targetIsImage = (target: CommentsRepository.Target) =>
    target.name === 'imageBlock' || target.name === 'imageInline';

  const contentCommentedOnText = useMemo(() => {
    if (!contentCommentedOnTargets) {
      return t('comments.notAvailable');
    }

    return contentCommentedOnTargets
      .map((target) => {
        if (targetIsImage(target)) {
          return t('comments.imageElement');
        }
        if (target.name === 'table') {
          return t('comments.tableElement');
        } else {
          return target.data;
        }
      })
      .join(' ');
  }, [contentCommentedOnTargets, t]);

  if (!contentCommentedOnTargets) {
    return <QText>{t('comments.commentedOnNotAvailable')}</QText>;
  }

  if (
    contentCommentedOnTargets.length === 1 &&
    targetIsImage(contentCommentedOnTargets[0])
  ) {
    return <QText>{t('comments.commentedOnImageElement')}</QText>;
  }

  if (
    contentCommentedOnTargets.length === 1 &&
    contentCommentedOnTargets[0].name === 'table'
  ) {
    return <QText>{t('comments.commentedOnTableElement')}</QText>;
  }

  const getDisplayText = (contentCommentedOnText: string) => {
    const displayLongText = `"${contentCommentedOnText}" `;
    const displayShortText = `"${
      contentCommentedOnText.substring(0, LETTER_LIMIT_CONTENT_COMMENTED_ON) +
      (contentCommentedOnText.length > LETTER_LIMIT_CONTENT_COMMENTED_ON
        ? '...'
        : '"')
    }`;

    return showAllContentCommentOn ? displayLongText : displayShortText;
  };

  return (
    <QText>
      <b>{t('comments.commentedOn')}</b>
      {getDisplayText(contentCommentedOnText)}
      {contentCommentedOnText.length > LETTER_LIMIT_CONTENT_COMMENTED_ON && (
        <QButton onClick={toggleShowAllContentCommentedOn} variant="link">
          {showAllContentCommentOn
            ? t('comments.showLess')
            : t('comments.showMore')}
        </QButton>
      )}
    </QText>
  );
};

export const ThreadComments = ({
  contentCommentedOnTargets,
  commentContent,
  commentIndex,
}: ThreadCommentsProps) => {
  return (
    <QStack>
      {commentIndex === 0 && (
        <ContentCommentedOn
          contentCommentedOnTargets={contentCommentedOnTargets}
        ></ContentCommentedOn>
      )}
      <QText data-cy="comment-content">{commentContent}</QText>
    </QStack>
  );
};

export const OpenCommentThread = ({ thread }: CommentThreadProps) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const activities = parseCommentInThread(thread, formatDate).map(
    (activity, index) => {
      const comment = thread.comments[index];
      if (comment.deleted_at) {
        return {
          ...activity,
          status: 'removed' as ActivityStatus,
          fullName:
            comment.attributes.deleted_by_user ?? t('comments.unknownUser'),
          actionTimestamp: formatDate(comment.deleted_at, true),
        };
      }
      return activity;
    },
  );
  const { status, comments, content_commented_on } = thread;
  const isOpenSuggestion =
    status === 'Open' &&
    !content_commented_on &&
    comments.some((comment) => comment?.attributes?.suggestionId);

  // filter open threads on suggestion, as we only show them once the suggestion has been accepted or declined
  if (isOpenSuggestion) {
    return null;
  }

  return (
    <QActivitySequence
      activities={activities}
      key={thread.id}
      data-cy="comment-history-thread-open-comment-element"
    />
  );
};

export const ResolvedCommentThread = ({
  thread,
  isMultiroot,
}: CommentThreadProps) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const activities = [
    ...parseCommentInThread(thread, formatDate).map((activity, index) => {
      const comment = thread.comments[index];
      if (isMultiroot && comment.deleted_at) {
        return {
          ...activity,
          status: 'removed' as ActivityStatus,
          fullName:
            comment.attributes.deleted_by_user ?? t('comments.unknownUser'),
          actionTimestamp: formatDate(comment.deleted_at, true),
        };
      }
      return activity;
    }),
    {
      status: 'markedResolved' as ActivityStatus,
      fullName: thread.deleted_by_user ?? t('comments.unknownUser'),
      actionTimestamp: thread.deleted_at
        ? formatDate(thread.deleted_at, true)
        : t('comments.unknownTime'),
    },
  ];
  return (
    <QActivitySequence
      activities={activities}
      key={thread.id}
      data-cy="comment-history-thread-resolved-comment-element"
    />
  );
};

export const RemovedCommentThread = ({ thread }: CommentThreadProps) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const activities = [
    ...parseCommentInThread(thread, formatDate),
    {
      status: 'removed' as ActivityStatus,
      fullName: thread.deleted_by_user ?? t('comments.unknownUser'),
      actionTimestamp: thread.deleted_at
        ? formatDate(thread.deleted_at, true)
        : t('comments.unknownTime'),
    },
  ];
  return (
    <QActivitySequence
      activities={activities}
      key={thread.id}
      data-cy="comment-history-thread-removed-comment-element"
    />
  );
};
